// =====================================================================================
// INVRT Radar — web console — revealed contact results
//
// Rendered after a successful reveal, in classic mode and in the chat thread alike. The
// provider payloads are genuinely untyped JSON, so every field is read defensively and a
// missing one shows as "—" rather than an empty cell pretending to be a blank value.
// =====================================================================================

import { Download } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "./ui/table";
import { downloadTextFile, timestampedFilename, toCsv } from "@/lib/csv";
import { formatCredits, formatCreditsWithUnit, readString } from "@/lib/format";
import type { RevealResponse, RevealedRecord } from "@/lib/types";

function personName(record: RevealedRecord): string {
  const direct = readString(record.person, "full_name", "name", "display_name");
  if (direct) return direct;
  const first = readString(record.person, "first_name", "firstName");
  const last = readString(record.person, "last_name", "lastName");
  const joined = [first, last].filter(Boolean).join(" ");
  return joined === "" ? "—" : joined;
}

const FIELDS = {
  name: personName,
  title: (record: RevealedRecord) => readString(record.person, "title", "job_title") ?? "—",
  company: (record: RevealedRecord) =>
    readString(record.company, "name", "company_name") ??
    readString(record.person, "company_name") ??
    "—",
  email: (record: RevealedRecord) => readString(record.person, "email", "work_email") ?? "—",
  phone: (record: RevealedRecord) =>
    readString(record.person, "phone", "direct_phone", "mobile") ?? "—",
  linkedin: (record: RevealedRecord) =>
    readString(record.person, "linkedin", "linkedin_url") ?? "—",
  location: (record: RevealedRecord) =>
    readString(record.person, "location", "city") ??
    readString(record.company, "location", "city") ??
    "—",
} as const;

export function RevealedContacts({ result }: { result: RevealResponse }) {
  function handleDownload(): void {
    const csv = toCsv(result.results, [
      { header: "Provider reference", value: (row) => row.provider_ref },
      { header: "Name", value: FIELDS.name },
      { header: "Title", value: FIELDS.title },
      { header: "Company", value: FIELDS.company },
      { header: "Email", value: FIELDS.email },
      { header: "Phone", value: FIELDS.phone },
      { header: "LinkedIn", value: FIELDS.linkedin },
      { header: "Location", value: FIELDS.location },
      { header: "Revealed at", value: (row) => row.revealed_at ?? "" },
    ]);
    downloadTextFile(timestampedFilename("radar-revealed-contacts"), csv);
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <CardTitle>
            {result.results.length === 1
              ? "1 contact revealed"
              : `${result.results.length} contacts revealed`}
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {result.unbilled ? (
              <Badge tone="warning">
                Not charged — Radar resolved this in your favour
              </Badge>
            ) : (
              <Badge tone="success">{formatCreditsWithUnit(result.credits_burned)} spent</Badge>
            )}
            {result.replayed ? (
              <Badge tone="neutral">Replayed — no second charge</Badge>
            ) : null}
            {result.balance_after !== null ? (
              <Badge tone="info">Balance {formatCredits(result.balance_after)}</Badge>
            ) : null}
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={handleDownload}>
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
          Download CSV
        </Button>
      </CardHeader>

      <CardContent>
        {result.unbilled ? (
          <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-warning">
            The reveal succeeded but the charge did not record. You keep the contacts and
            were not billed. INVRT has the record and will reconcile it.
          </p>
        ) : null}

        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Title</TableHeaderCell>
              <TableHeaderCell>Company</TableHeaderCell>
              <TableHeaderCell>Email</TableHeaderCell>
              <TableHeaderCell>Phone</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.results.map((record) => (
              <TableRow key={record.provider_ref}>
                <TableCell className="font-medium">{FIELDS.name(record)}</TableCell>
                <TableCell className="text-ink-muted">{FIELDS.title(record)}</TableCell>
                <TableCell className="text-ink-muted">{FIELDS.company(record)}</TableCell>
                <TableCell className="font-mono text-xs">{FIELDS.email(record)}</TableCell>
                <TableCell className="font-mono text-xs">{FIELDS.phone(record)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
